/**
 * Runtime selector — compact inline org → user picker.
 * Renders in the RuntimeOverview page header (not sidebar).
 */

import { ChevronDown, Loader2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import { useRuntimeContext } from "@/hooks/useRuntimeContext";
import { useRuntimeVersions } from "@/hooks/useRuntimeVersions";
import { VersionSkewBadge } from "@/components/shared/VersionSkewBadge";
import type { Organization, OrgUser } from "@/types/platform";

export function RuntimeSelector() {
  const { target, selectRuntime, clearRuntime, isLocalDev, loading, error } = useRuntimeContext();
  const api = usePlatformApi();
  // §22 — per-runtime skew at a glance (omitted when the batch
  // endpoint isn't live; never computes skew client-side).
  const { byUserId, targetVersion, available: versionsAvailable } = useRuntimeVersions();

  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>("");
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const loadOrgs = useCallback(async () => {
    setLoadingOrgs(true);
    try {
      const res = await api.listOrgs({ limit: 50 });
      setOrgs(res.data.filter((o) => o.status === "ACTIVE"));
    } catch { /* ignore */ } finally {
      setLoadingOrgs(false);
    }
  }, [api]);

  const loadUsers = useCallback(async (orgId: string) => {
    setLoadingUsers(true);
    setUsers([]);
    try {
      const res = await api.listOrgUsers(orgId, { status: "ACTIVE", limit: 50 });
      setUsers(res.data);
    } catch { /* ignore */ } finally {
      setLoadingUsers(false);
    }
  }, [api]);

  useEffect(() => {
    if (open && orgs.length === 0) { loadOrgs(); }
  }, [open, orgs.length, loadOrgs]);

  useEffect(() => {
    if (selectedOrg) { loadUsers(selectedOrg); }
  }, [selectedOrg, loadUsers]);

  const handleConnect = async (user: OrgUser) => {
    const org = orgs.find((o) => o.orgId === user.orgId);
    await selectRuntime(
      user.orgId,
      user.userId,
      org?.name ?? user.orgId,
      user.displayName,
      org?.slug ?? user.orgId,
    );
    setOpen(false);
    setSelectedOrg("");
    setUsers([]);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted transition-colors hover:bg-accent hover:text-foreground"
      >
        {isLocalDev ? "Switch Runtime" : "Change"}
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {error && <p className="absolute right-0 top-full mt-1 text-[10px] text-critical">{error}</p>}

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-72 rounded-lg p-3 cd-panel">
          {!isLocalDev && (
            <button type="button" onClick={() => { clearRuntime(); setOpen(false); }}
              className="mb-2 flex w-full items-center gap-1 rounded px-2 py-1 text-xs text-muted hover:bg-accent hover:text-foreground">
              <X className="h-3 w-3" /> Back to Local Dev
            </button>
          )}

          <select value={selectedOrg} onChange={(e) => setSelectedOrg(e.target.value)}
            className="mb-2 w-full rounded border border-border bg-panel-2 px-2 py-1.5 text-xs">
            <option value="">Select organization...</option>
            {orgs.map((o) => <option key={o.orgId} value={o.orgId}>{o.name}</option>)}
          </select>

          {loadingOrgs && <p className="py-1 text-center text-[10px] text-muted-foreground"><Loader2 className="inline h-3 w-3 animate-spin" /></p>}

          {selectedOrg && (
            <div className="max-h-48 space-y-0.5 overflow-y-auto">
              {loadingUsers ? (
                <p className="py-2 text-center text-[10px] text-muted-foreground"><Loader2 className="inline h-3 w-3 animate-spin" /></p>
              ) : users.length === 0 ? (
                <p className="py-2 text-center text-[10px] text-muted-foreground">No active users</p>
              ) : (
                users.map((u) => (
                  <button key={u.userId} type="button" onClick={() => handleConnect(u)} disabled={loading}
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-accent disabled:opacity-50">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{u.displayName}</p>
                      <p className="truncate text-[10px] text-muted-foreground">{u.email}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {versionsAvailable && byUserId.has(u.userId) && (
                        <VersionSkewBadge
                          skew={byUserId.get(u.userId)!.skew}
                          targetVersion={targetVersion}
                          className="px-1.5 py-0 text-[9px]"
                        />
                      )}
                      {u.containerEndpoint
                        ? <span className="text-[9px] text-healthy">provisioned</span>
                        : <span className="text-[9px] text-faint">no instance</span>}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}

          {loading && <p className="mt-1 py-1 text-center text-[10px] text-muted-foreground"><Loader2 className="inline h-3 w-3 animate-spin" /> Connecting...</p>}
        </div>
      )}
    </div>
  );
}
